import type { Gdal } from "./gdalRuntime";

/**
 * How long the runtime may take to start before the load is treated as failed.
 *
 * gdal3.js never rejects when its worker fails to start, so without this a
 * blocked or broken download would leave callers waiting forever.
 */
export const LOAD_TIMEOUT_MS = 120_000;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`GDAL runtime did not start within ${ms / 1000} seconds`)), ms);
  });

  return Promise.race([promise, expiry]).finally(() => clearTimeout(timer));
};

let gdalPromise: Promise<Gdal> | undefined;

/** The GDAL runtime, downloaded on first use and reused thereafter. */
export const loadGdal = (): Promise<Gdal> => {
  gdalPromise ??= import("./gdalRuntime")
    .then(({ initialiseGdal }) => withTimeout(initialiseGdal(), LOAD_TIMEOUT_MS))
    .catch((error) => {
      // Discard the rejected promise so a failed download can be retried.
      gdalPromise = undefined;
      throw error;
    });

  return gdalPromise;
};
